import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { EmailOnBehalfTypes } from '~/models/EmailOnBehalfSettingsModel';
import { selectors } from '~/store/state-selectors';
import gmailLogo from '../../../assets/Gmail.png';
import outlookLogo from '../../../assets/Outlook.png';
import { getEmailProviderDetails } from './NotificationSettings.utils';
import styles from './SendEmailOnBehalfModal.module.scss';
export function SendEmailOnBehalfModal(param) {
    let { dismissModal } = param;
    const { t: notificationSettingsTexts } = useLocaleKeys('pages.notificationSettings');
    const { t: commonTexts } = useLocaleKeys('common');
    const businessId = useStore(selectors.user.businessId);
    const { api } = useStoreActions();
    const changeLocation = useIoContext().browser.changeLocation;
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { logBI } = useStoreActions();
    const connectEmail = async (provider)=>{
        const providerDetails = getEmailProviderDetails(provider);
        logBI(providerDetails.biCodes.connect, {
            vendorId: businessId
        });
        if (businessId) {
            startLoader();
            const result = await handleRequest(api.notification.emailOnBehalf.connectEmailOnBehalf({
                businessId,
                provider,
                pageUrl: window.location.origin + window.location.pathname
            }));
            if (result.success) {
                changeLocation(result.data);
            } else {
                logBI(providerDetails.biCodes.authError, {
                    vendorId: businessId
                });
                showErrorToast(commonTexts.toasts.failedToConnectProvider({
                    provider
                }), "start-".concat(provider, "-flow-error-toast"));
            }
            stopLoader();
        } else {
            logBI(providerDetails.biCodes.authError || 18503, {
                vendorId: businessId
            });
            showErrorToast(commonTexts.toasts.pleaseRefreshAndTryAgain(), 'no-business-id-error-toast');
        }
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'send_email_on_behalf_confirm'
            },
            secondaryButton: {
                name: 'send_email_on_behalf_cancel'
            }
        },
        width: "640px",
        title: notificationSettingsTexts.sendEmailTitle(),
        testId: "send-email-on-behalf-modal",
        onSecondaryButtonClick: dismissModal,
        onCloseButtonClick: dismissModal,
        content: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    variant: "paragraph1",
                    className: styles.description,
                    children: [
                        "Using your own domain to send emails ensures brand consistency, making your communications more recognizable and trustworthy to clients. It also improves email deliverability, reducing the chances of your emails being marked as spam. ",
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx("br", {}),
                        "Please choose an email platform to connect:"
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.buttons,
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.MailButton,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: styles.MailButtonImage,
                                    children: [
                                        /*#__PURE__*/ _jsx("img", {
                                            src: gmailLogo,
                                            width: 35,
                                            height: 35
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph1",
                                            children: notificationSettingsTexts.gmail()
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    bi: {
                                        name: 'connect_gmail_for_send_on_behalf'
                                    },
                                    className: styles.ConnectButton,
                                    size: "small",
                                    skin: "primaryOutline",
                                    testId: "connect-gmail-button",
                                    onClick: ()=>connectEmail(EmailOnBehalfTypes.Gmail),
                                    children: commonTexts.buttons.connectYourGmailAccount()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.MailButton,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: styles.MailButtonImage,
                                    children: [
                                        /*#__PURE__*/ _jsx("img", {
                                            src: outlookLogo,
                                            width: 35,
                                            height: 35
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph1",
                                            children: notificationSettingsTexts.outlook()
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    bi: {
                                        name: 'connect_outlook_for_send_on_behalf'
                                    },
                                    className: styles.ConnectButton,
                                    size: "small",
                                    skin: "primaryOutline",
                                    testId: "connect-outlook-button",
                                    onClick: ()=>connectEmail(EmailOnBehalfTypes.Outlook),
                                    children: commonTexts.buttons.connectYourOutlookAccount()
                                })
                            ]
                        })
                    ]
                })
            ]
        }),
        secondaryButtonLabel: commonTexts.buttons.cancel()
    });
}
